import { AUTH_MODE } from '@utils/constants';
import { buildQueryString } from '@utils/Functions';
import * as qs from 'qs';
import { authApi } from '@routes/api';
import axios from '@services/InterceptorsForAxiosRequestsBase';

export const localStorageKeys = {
  AUTH_TOKEN: 'portal/auth_token',
  AUTH_USER: 'portal/user_info',
};
const { AUTH_TOKEN, AUTH_USER } = localStorageKeys;

export const storage =
  'window' in globalThis ? globalThis.window.localStorage : null;

class AuthService {
  async getToken(credentials) {
    const filters = buildQueryString({});
    let path = authApi.token(filters);

    if (credentials?.authentication === AUTH_MODE.INTERNAL) {
      const filters = buildQueryString({
        id_user: credentials?.id_user,
        authentication: credentials?.authentication,
      });
      path = authApi.token(filters);
    }

    const formData = qs.stringify({
      username: credentials.username,
      password: credentials.password,
    });

    const internalData = qs.stringify({
      username: credentials.username,
      password: 'HARDCODED',
      roles: credentials?.roles,
      id_user: credentials.id_user,
    });

    const body =
      credentials?.authentication === AUTH_MODE.INTERNAL
        ? internalData
        : formData;

    const response = await axios.post(path, body);
    return response;
  }

  async login(credentials) {
    const response = await this.getToken(credentials);
    return response;
  }

  getAuthHeaderBlob() {
    const typeToken = this.getTypeToken();
    const accessToken = this.getAccessToken();
    if (typeToken && accessToken) {
      return {
        responseType: 'blob',
        headers: {
          Accept: 'application/json',
          Authorization: typeToken + ' ' + accessToken,
        },
      };
    }
    return null;
  }

  saveTokenInLocalStorage = (response) => {
    try {
      if (!response || typeof response !== 'object') {
        throw new TypeError('Invalid response');
      }
      const { data } = response;
      const accessToken = data?.access_token;
      if (!accessToken) return false;
      storage.setItem(AUTH_TOKEN, JSON.stringify(data));
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  getUserInfo() {
    if (!storage) return '';
    try {
      const authUser = JSON.parse(storage.getItem(AUTH_USER));
      return authUser || '';
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  getRoles() {
    const filters = buildQueryString({});
    const path = authApi.roles(filters);
    return axios.get(path);
  }

  getUserId() {
    const { id_user } = this.getUserInfo();
    return id_user || '';
  }

  hasRotatingShifts() {
    const { horarios_rotativos } = this.getUserInfo();
    return horarios_rotativos;
  }

  getCurrentUserRoles() {
    const { roles } = this.getUserInfo();
    return roles || [];
  }

  isAuthenticated = () => {
    const token = this.getAccessToken();
    return !!token;
  };

  saveAuthToken(authToken) {
    if (!storage) return false;
    try {
      storage.setItem(AUTH_TOKEN, JSON.stringify(authToken));
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  getAuthToken() {
    if (!storage) return '';
    try {
      const authToken = JSON.parse(storage.getItem(AUTH_TOKEN));
      return authToken || '';
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  getAuthHeader() {
    const typeToken = this.getTypeToken();
    const accessToken = this.getAccessToken();
    if (typeToken && accessToken) {
      return {
        headers: {
          Accept: 'application/json',
          Authorization: typeToken + ' ' + accessToken,
        },
      };
    }
    return null;
  }

  getAuthorizationHeader() {
    return this.getAuthHeader().headers['Authorization'];
  }

  getAccessToken() {
    return this.getAuthToken()?.access_token || null;
  }

  getTypeToken() {
    return this.getAuthToken()?.token_type || null;
  }

  refreshToken() {
    const token = this.getAuthToken();
    if (token === null) {
      return Promise.reject(new Error('refresh-token-not-found'));
    }
    const filters = buildQueryString({});
    const path = authApi.refreshToken(filters);
    return axios.post(path, token);
  }

  logout() {
    try {
      storage.removeItem(AUTH_TOKEN);
      storage.removeItem(AUTH_USER);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  isLoggedUser = (idUser) => {
    return idUser === String(this.getUserId());
  };

  getAuthHeaderMultipart() {
    const typeToken = this.getTypeToken();
    const accessToken = this.getAccessToken();
    if (typeToken && accessToken) {
      return {
        headers: {
          Accept: 'application/json',
          Authorization: typeToken + ' ' + accessToken,
          'content-type': 'multipart/form-data',
        },
      };
    }
    return null;
  }

  getAuthHeaderMultipartBlob() {
    const typeToken = this.getTypeToken();
    const accessToken = this.getAccessToken();
    if (typeToken && accessToken) {
      return {
        headers: {
          Accept: 'application/json',
          Authorization: typeToken + ' ' + accessToken,
        },
        responseType: 'blob',
      };
    }
    return null;
  }

  statusControl() {
    const filters = buildQueryString({});
    const path = authApi.statusControl(filters);
    return axios.get(path);
  }
}

export default new AuthService();
